'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function getMeasurementId(): string | null {
  const id = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!id || id === 'your-google-analytics-id-here') return null;
  return id;
}

function injectGAScript(measurementId: string) {
  if (typeof window === 'undefined') return;
  // Already injected
  if (document.querySelector(`script[src*="${measurementId}"]`)) return;

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
}

function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem('ledgerpay_cookie_consent');
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return parsed?.analytics === true;
  } catch {
    return false;
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = getMeasurementId();
    if (!measurementId) return;

    // Only load GA if consent has already been granted
    if (hasAnalyticsConsent()) {
      injectGAScript(measurementId);
    }

    // Listen for consent being granted after the banner interaction
    const handleConsent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.analytics === true) {
        injectGAScript(measurementId);
      }
    };
    window.addEventListener('ledgerpay:consent', handleConsent);
    return () => window.removeEventListener('ledgerpay:consent', handleConsent);
  }, []);

  // Track page views — only fires if GA is loaded (i.e. consent was granted)
  useEffect(() => {
    const measurementId = getMeasurementId();
    if (!measurementId) return;
    if (typeof window === 'undefined' || !window.gtag) return;
    if (!hasAnalyticsConsent()) return;

    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: document.title,
    });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, unknown> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    if (!getMeasurementId()) return;
    if (!hasAnalyticsConsent()) return;
    window.gtag('event', eventName, eventParams);
  }
}
